import React, { useCallback, useContext, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDesktop, faUsers } from "@fortawesome/pro-regular-svg-icons";
import { faBan } from "@fortawesome/pro-solid-svg-icons";
import BackButton from "../../components/BackButton";
import { ClavaContext } from "../../../config/contexts";
import { IDType } from "../../../config/types";
import MatchdaySelector, { MatchdaySelectorItem } from "../../components/MatchdaySelector";
import { showTranslated, translate } from "../../../config/translator";
import { getMainLeague, isAdmin, isInsider } from "../../../config/utils";
import ClavaImage from "../../components/ClavaImage";
import SquadManagement from "./SquadManagement";
import WidgetManagement from "./WidgetManagement";
import TabletPageDefault, { TabletContainer } from "../../components/Layout/TabletLayout";
import { TwoColumnGrid } from "../../components/Layout/TwoColumn";
import RoundedRectangle from "../../components/RoundedRectangle";
import { LanguageLocaleEnum, TeamListMinimum } from "../../../client/api";
import { trackAmplitude } from "../../../config/amplitude";

const createTeamLabel = (
  teamName: TeamListMinimum["name"],
  mainLeague: ReturnType<typeof getMainLeague>,
  l: LanguageLocaleEnum
): string => {
  const leagueName = mainLeague ? showTranslated(mainLeague.name, l) : "";
  const separator = mainLeague ? " | " : "";
  return `${showTranslated(teamName, l)}${separator}${leagueName}`;
};

const getTeamItem = (team: TeamListMinimum, l: LanguageLocaleEnum): MatchdaySelectorItem => {
  const mainLeague = getMainLeague(team.leagues);
  return {
    key: team.id,
    label: createTeamLabel(team.name, mainLeague, l)
  };
};

const getButtonClassName = (isActive: boolean): string => {
  const baseClasses = "rounded-lg flex flex-row items-center text-font dark:text-font-dark my-1 w-full py-1 px-2 hover:text-primary hover:bg-primary-20 transition-colors";
  const activeClass = isActive ? "text-primary" : "";
  return `${baseClasses} ${activeClass}`;
};

const TeamManagement: React.FC = () => {
  const { user, l } = useContext(ClavaContext);
  const initialTeamId = user.groups[0]?.team?.id;
  const [team, setTeam] = useState<IDType | undefined>(initialTeamId);

  const [type, setType] = useState<"squad" | "widgets">("squad");

  const selectedTeam = useMemo(() =>
      user.groups
        .map(g => g.team)
        .find(t => t.id === team),
    [team, user.groups]
  );

  const teamItems = useMemo<MatchdaySelectorItem[]>(() =>
      user.groups.map(g => getTeamItem(g.team, l)),
    [l, user.groups]
  );

  const selectedMainLeague = useMemo(() =>
      selectedTeam ? getMainLeague(selectedTeam.leagues) : undefined,
    [selectedTeam]
  );

  const handleTeamChange = useCallback((val: number | undefined) => {
    setTeam(val);
    trackAmplitude("tm_team", { teamId: val });
  }, []);

  const handleTypeChange = useCallback((newType: "squad" | "widgets") => {
    setType(newType);
    trackAmplitude("tm_tab", { tab: newType });
  }, []);

  const insider = useMemo(() => isInsider(user) || isAdmin(user), [user]);
  if (!insider) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <FontAwesomeIcon icon={faBan} className="text-loss w-12 h-12 mb-6" />
        <span>{translate("onlyInsider", l)}</span>
      </div>
    );
  }
  return (
    <TabletPageDefault>
      <TabletContainer>
        <TwoColumnGrid className="xl:grid-cols-3 xl:gap-8 mt-6">
          <div className="min-w-0 w-full xl:col-span-1 xl:w-72 xl:-ml-6">
            <div className="mb-4">
              <MatchdaySelector
                value={team}
                onChange={handleTeamChange}
                items={teamItems}
                placeholder={translate("chooseTeam", l)}
                withoutChoose
                transparent
                containerClassName="relative overflow-hidden h-12 flex-shrink-0 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-10 w-full"
                className="text-left w-full"
              />
            </div>
            {selectedTeam && (
              <div
                className="rounded-2xl border border-light-gray dark:border-light-gray-dark bg-bg dark:bg-bg-dark shadow-sm p-2">
                <div>
                  <div className="rounded-xl bg-primary-10 p-2">
                    <button
                      type="button"
                      onClick={() => handleTypeChange("squad")}
                      className={getButtonClassName(type === "squad")}
                    >
                      <FontAwesomeIcon icon={faUsers} className="w-4 h-4 mr-2" />
                      {translate("squad", l)}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTypeChange("widgets")}
                      className={getButtonClassName(type === "widgets")}
                    >
                      <FontAwesomeIcon icon={faDesktop} className="w-4 h-4 mr-2" />
                      {translate("webwidgets", l)}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="min-w-0 xl:col-span-2 xl:-ml-24">
            <RoundedRectangle
              className="rounded-xl relative mb-6 bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pt-12"
              hasBorder
              hasTitle={false}
            >
              <BackButton to="/home" />
              {selectedTeam && (
                <div className="flex items-center gap-4 px-6 xl:px-8 pb-8 pt-8">
                  <ClavaImage width={64} image={selectedTeam.emblem} className="shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-base font-medium text-font dark:text-font-dark">
                      {showTranslated(selectedTeam.name, l)}
                    </span>
                    {selectedMainLeague && (
                      <span className="text-sm font-normal text-font-600 dark:text-font-600-dark">
                        {showTranslated(selectedMainLeague.name, l)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </RoundedRectangle>
            {selectedTeam && (
              type === "squad"
                ? <SquadManagement team={selectedTeam} />
                : <WidgetManagement team={selectedTeam} />
            )}
          </div>
        </TwoColumnGrid>
      </TabletContainer>
    </TabletPageDefault>
  );
};

export default TeamManagement;
